import { type FC, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2, Pen, Plus, Save, Trash2, Undo2, X } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { compareDesc, format, parseISO } from 'date-fns';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { createAssetNotes, deleteAssetNotes, undeleteAssetNotes, updateAssetNotes } from '@/services/asset.service';
import { renderField } from '@/components/inspector/entity-fields/form/field-components';
import { ModalId, useCloseModal, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { OptionAction } from '@/components/inspector/entity-fields/form/option-action';
import { DialogClose, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { CreateNoteDialog } from '@/components/create-dialogs/create-note-dialog';
import { Options } from '@/components/inspector/entity-fields/form/options';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { ToastAction, ToastVariant } from '@/components/ui/toast';
import { useCommandContext } from '@/context/CommandContext';
import { NoteDialog } from '@/components/note-dialog';
import { useNotes } from '@/hooks/notes/useNotesList';
import { useToast } from '@/components/ui/use-toast';
import { error as errorLog } from '@/utilities/log';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { QueryKey } from '@/lib/queryKeys';
import { type Asset } from '@/types/asset';
import { useTranslations } from 'next-intl';

type OptionItemProps = {
  option: {
    id: string;
    assetId: string;
    createdAt: string;
    createdBy: { name: string };
    note: string;
  };
  onEdit: () => void;
  readOnly?: boolean;
  onDelete: (assetId: string, noteId: string) => void;
};

export const OptionItem: FC<OptionItemProps> = ({
  option: { id, assetId, createdAt, createdBy, note },
  onEdit,
  readOnly,
  onDelete,
}) => {
  const openModal = useOpenModal();
  const tCommon = useTranslations('common');
  const tDialogs = useTranslations('dialogs');
  const tNotes = useTranslations('inspector.notes');

  return (
    <div key={id} className="flex w-full flex-row gap-2 px-3">
      <div className="flex grow flex-col">
        <p title={createdBy?.name} className="line-clamp-1 text-sm break-all" data-cy="asset-note-created-by">
          {createdBy?.name}
        </p>
        {createdAt && (
          <p className="text-xs text-text-gray-alternate" data-cy="asset-note-created-at">
            {format(createdAt, 'yyyy-MM-dd HH:mm')}
          </p>
        )}
        {note && (
          <p
            className="mt-0.5 line-clamp-5 text-xs wrap-anywhere whitespace-pre-line text-text-gray hover:cursor-pointer"
            title={tNotes('doubleClickHint')}
            onDoubleClick={() => openModal(ModalId.AssetNote, { note })}
            data-cy="asset-note"
          >
            {note}
          </p>
        )}
      </div>
      {!readOnly && (
        <div className="mt-1 flex gap-x-1">
          <OptionAction icon={Pen} onClick={onEdit} data-cy="edit-asset-note" tooltip={tCommon('edit')} />
          <OptionAction
            icon={Trash2}
            onClick={() =>
              openModal(ModalId.DeleteConfirmationAlertDialog, {
                'content': tDialogs('deleteNoteConfirmation'),
                'onClick': () => onDelete(assetId, id),
                'data-cy': 'delete-asset-note',
              })
            }
            data-cy="delete-asset-note"
            variant="destructive"
            tooltip={tCommon('delete')}
          />
        </div>
      )}
    </div>
  );
};

export const NotesAccordion = ({
  disabled,
  asset,
  readOnly = false,
  enabled,
}: {
  disabled: boolean;
  asset: Asset;
  readOnly?: boolean;
  enabled: boolean;
}) => {
  const [selectedNote, setSelectedNote] = useState<{ id?: string; note: string } | null>(null);

  const tErrors = useTranslations('errors');
  const tCommon = useTranslations('common');
  const tDialogs = useTranslations('dialogs');
  const tNotes = useTranslations('inspector.notes');
  const tToasts = useTranslations('toasts');
  const tValidation = useTranslations('validation');

  const queryClient = useQueryClient();
  const openModal = useOpenModal();
  const closeModal = useCloseModal();

  const { apply, undo } = useCommandContext();
  const { toast } = useToast();

  const { data: { notes = [] } = {}, notesIsFetching } = useNotes(asset?.id, {
    enabled: enabled,
  });

  const formFields: Array<any> = useMemo(
    () => [
      {
        name: 'Note',
        slug: 'note',
        type: 'text',
        value: '',
        rows: 6,
        validation: z
          .string({
            error: (issue) => {
              if (!issue.input) {
                return tValidation('fieldMustBeString', { field: tValidation('fields.noteField') });
              }
            },
          })
          .min(1, {
            message: tValidation('fieldRequired', { field: tValidation('fields.note') }),
          })
          .max(5000, { message: tValidation('fieldMaxLength', { field: tValidation('fields.note'), max: 5000 }) }),
      },
    ],
    [tValidation],
  );

  const formSchema = z.object(
    formFields.reduce((acc, field) => {
      acc[field.slug] = field.validation;
      return acc;
    }, {}),
  );

  const defaultValues = formFields.reduce(
    (acc, field) => {
      acc[field.slug] = field.value;
      return acc;
    },
    {} as Record<string, any>,
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const deleteNoteWithAuth = useAuthenticatedQueryFn(deleteAssetNotes);

  const deleteNoteMutation = useMutation({
    mutationFn: deleteNoteWithAuth,
    onSuccess: () => {
      toast({
        title: tToasts('deleteNoteMutation.title'),
        description: tToasts('deleteNoteMutation.description'),
        action: (
          <ToastAction altText={tToasts('deleteNoteMutation.undoAlt')} onClick={() => undo()} icon={Undo2}>
            {tCommon('undo')}
          </ToastAction>
        ),
      });

      void queryClient.invalidateQueries({ queryKey: [QueryKey.NotesList, asset?.id] });
    },
    onError: (err) => {
      errorLog('Action failed:', err);

      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description: err.message ?? tErrors('toastDefaultDescription'),
      });
    },
  });

  const createAssetNoteWithAuth = useAuthenticatedQueryFn(createAssetNotes);
  const updateAssetNoteWithAuth = useAuthenticatedQueryFn(updateAssetNotes);
  const undeleteNoteWithAuth = useAuthenticatedQueryFn(undeleteAssetNotes);

  const createOrUpdateNoteMutation = useMutation({
    mutationFn: (values: z.infer<typeof formSchema>) =>
      selectedNote?.id
        ? updateAssetNoteWithAuth({ assetId: asset?.id, noteId: selectedNote.id, body: values })
        : createAssetNoteWithAuth({ assetId: asset?.id, body: values }),
    onSuccess: () => {
      toast({
        title: selectedNote?.id ? tToasts('updateNoteMutation.title') : tToasts('createNoteMutation.title'),
        description: selectedNote?.id
          ? tToasts('updateNoteMutation.description')
          : tToasts('createNoteMutation.description'),
      });

      closeModal(ModalId.CreateAssetNotes);

      void queryClient.invalidateQueries({ queryKey: [QueryKey.NotesList, asset?.id] });
    },
    onError: (err) => {
      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description: err.message ?? tErrors('toastDefaultDescription'),
      });
    },
  });

  const undeleteNoteMutation = useMutation({
    mutationFn: undeleteNoteWithAuth,
    onSuccess: () => {
      toast({
        title: tToasts('undeleteNoteMutation.title'),
        description: tToasts('undeleteNoteMutation.description'),
      });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.NotesList, asset?.id] });
    },
    onError: (err: any) => {
      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description: err.message ?? tErrors('toastDefaultDescription'),
      });
    },
  });

  const openNoteModal = (note?: { id: string; note: string }) => {
    setSelectedNote(note ?? { note: '' });
    form.reset({ note: note?.note ?? '' });
    openModal(ModalId.CreateAssetNotes);
  };

  useEffect(() => {
    form.reset({ note: selectedNote?.note ?? '' });
  }, [selectedNote, form]);

  const isLoading = createOrUpdateNoteMutation.isPending;

  const onDelete = (assetId: string, noteId: string) => {
    const deleteNoteCommand = {
      apply: () => deleteNoteMutation.mutateAsync({ assetId, noteId }),
      undo: () => undeleteNoteMutation.mutateAsync({ assetId, noteId }),
      redo: () => deleteNoteMutation.mutateAsync({ assetId, noteId }),
      message: 'Delete note',
    };

    apply(deleteNoteCommand);
  };

  return (
    <div className="flex h-full flex-col">
      <Options
        disabled={disabled || notesIsFetching}
        handleAddNewOption={() => openNoteModal()}
        addButtonPosition="top"
        className="h-full gap-y-5 py-2"
        readOnly={readOnly}
        fullHeight
        emptyState={
          <p className="px-4 py-12 text-center text-balance text-icon-base">
            {tNotes('empty')}{' '}
            {!readOnly && (
              <>
                {tNotes.rich('addNoteHint', {
                  plusIcon: () => (
                    <span className="whitespace-nowrap">
                      {`"`}
                      <Plus className="inline icon-4" />
                      {`"`}
                    </span>
                  ),
                })}
              </>
            )}
          </p>
        }
      >
        {
          // Sort notes by creation date desc
          notes
            .sort((a, b) => compareDesc(parseISO(a.createdAt), parseISO(b.createdAt)))
            .map((note) => (
              <OptionItem
                key={note.id}
                option={note}
                onEdit={() => openNoteModal(note)}
                onDelete={() => onDelete(asset.id, note.id)}
                readOnly={readOnly}
              />
            ))
        }
      </Options>
      <NoteDialog />
      {!readOnly && (
        <CreateNoteDialog title={selectedNote?.id ? tDialogs('editNoteTitle') : tDialogs('createNoteTitle')}>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((values) => createOrUpdateNoteMutation.mutate(values))}
              className="space-y-px"
            >
              <DialogDescription className="px-3 py-6">
                {formFields.map((field) => renderField(form, field))}
              </DialogDescription>
              <DialogFooter>
                <DialogClose asChild>
                  <Button
                    variant="secondary"
                    disabled={isLoading}
                    data-cy="create-edit-asset-note-dismiss-button"
                    icon={X}
                    onClick={() => closeModal(ModalId.CreateAssetNotes)}
                  >
                    {tCommon('cancel')}
                  </Button>
                </DialogClose>
                <Button
                  disabled={isLoading}
                  variant="success"
                  data-cy="create-edit-asset-note-create-button"
                  {...(isLoading && { iconClassName: 'animate-spin' })}
                  icon={isLoading ? Loader2 : selectedNote?.id ? Save : Plus}
                >
                  {isLoading
                    ? selectedNote?.id
                      ? tCommon('saving')
                      : tCommon('creating')
                    : selectedNote?.id
                      ? tCommon('save')
                      : tCommon('create')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </CreateNoteDialog>
      )}
    </div>
  );
};
